import { useState, useEffect, useRef } from "react";

const BASE = "https://cbackend.xraidigital.com/api";

const TEAL = "#11a8a4";
const BAR_COLORS = ["#11a8a4", "#0d8f8b", "#14b8a6", "#2dd4bf", "#5eead4", "#99f6e4"];

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const DARK = {
  pageBg: "#020817",
  navBg: "#0A0F1E",
  navBorder: "#1E293B",
  cardBg: "#0F172A",
  cardBorder: "#1E293B",
  inputBg: "#0F172A",
  inputBorder: "#1E293B",
  dropdownBg: "#0A0F1E",
  rowHover: "rgba(255,255,255,0.03)",
  trackBg: "#1E293B",
  text1: "#F1F5F9",
  text2: "#E2E8F0",
  text3: "#94A3B8",
  text4: "#64748B",
  text5: "#475569",
  text6: "#334155",
  donutTrack: "#1E293B",
  donutLabel: "#94A3B8",
  donutCount: "#F1F5F9",
  thToggleBg: "#1E293B",
  thToggleKnob: "#334155",
  isDark: true,
};

const LIGHT = {
  pageBg: "#F0F4F8",
  navBg: "#FFFFFF",
  navBorder: "#E2E8F0",
  cardBg: "#FFFFFF",
  cardBorder: "#E2E8F0",
  inputBg: "#F8FAFC",
  inputBorder: "#CBD5E1",
  dropdownBg: "#FFFFFF",
  rowHover: "rgba(0,0,0,0.02)",
  trackBg: "#E2E8F0",
  text1: "#0F172A",
  text2: "#1E293B",
  text3: "#475569",
  text4: "#64748B",
  text5: "#94A3B8",
  text6: "#CBD5E1",
  donutTrack: "#E2E8F0",
  donutLabel: "#64748B",
  donutCount: "#0F172A",
  thToggleBg: "#E2E8F0",
  thToggleKnob: "#FFFFFF",
  isDark: false,
};

const fmt = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (n) => Number(n || 0).toFixed(2) + "%";
const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${day} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m) - 1]} ${y}`;
};

// ─── Mini bar chart ───────────────────────────────────────────────────────────
function MiniBarChart({ data, colors, th }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.value), 1);
  const W = 280, H = 80, barW = Math.floor((W - (data.length - 1) * 8) / data.length);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {data.map((d, i) => {
        const bh = Math.max(4, Math.round((d.value / max) * (H - 20)));
        const x = i * (barW + 8);
        const y = H - bh - 18;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={3} fill={colors[i % colors.length]} opacity={0.85} />
            <text x={x + barW / 2} y={H - 2} textAnchor="middle" fontSize={10} fill={th.text3}>
              {d.label.length > 8 ? d.label.slice(0, 7) + "…" : d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Donut chart ──────────────────────────────────────────────────────────────
function DonutChart({ segments, size = 110, th }) {
  const r = 38, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, d) => s + d.value, 0) || 1;
  let offset = 0;
  const slices = segments.map((seg) => {
    const pct = seg.value / total;
    const dash = pct * circ;
    const slice = { ...seg, dash, offset: offset * circ };
    offset += pct;
    return slice;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={th.donutTrack} strokeWidth={14} />
      {slices.map((sl, i) => (
        <circle
          key={i} cx={cx} cy={cy} r={r} fill="none" stroke={sl.color} strokeWidth={14}
          strokeDasharray={`${sl.dash} ${circ - sl.dash}`}
          strokeDashoffset={circ / 4 - sl.offset} strokeLinecap="butt"
        />
      ))}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={11} fill={th.donutLabel}>total</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={13} fill={th.donutCount} fontWeight="600">
        {segments.length}
      </text>
    </svg>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, accent, danger, success, th }) {
  const valColor = danger ? "#EF4444" : success ? "#10B981" : th.text1;
  const iconBg = danger ? "rgba(239,68,68,0.1)" : success ? "rgba(16,185,129,0.1)"
    : accent ? "rgba(17,168,164,0.12)" : th.isDark ? "rgba(148,163,184,0.08)" : "rgba(0,0,0,0.04)";
  const iconColor = danger ? "#EF4444" : success ? "#10B981" : accent ? TEAL : th.text3;
  return (
    <div
      style={{
        background: th.cardBg, border: `1px solid ${th.cardBorder}`, borderRadius: 16,
        padding: "20px 22px", display: "flex", flexDirection: "column", gap: 10,
        transition: "box-shadow 0.2s, transform 0.2s",
        boxShadow: th.isDark ? "none" : "0 1px 4px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 4px 20px rgba(17,168,164,0.12)`; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = th.isDark ? "none" : "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, color: th.text4, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>{label}</span>
        <span style={{
          width: 34, height: 34, borderRadius: 10, background: iconBg,
          display: "flex", alignItems: "center", justifyContent: "center", color: iconColor, fontSize: 17,
        }}>{icon}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: valColor, lineHeight: 1.1, letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: th.text4, display: "flex", alignItems: "center", gap: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────
function Card({ children, style, th }) {
  return (
    <div style={{
      background: th.cardBg, border: `1px solid ${th.cardBorder}`, borderRadius: 16,
      padding: "22px 24px", boxShadow: th.isDark ? "none" : "0 1px 4px rgba(0,0,0,0.05)",
      ...style,
    }}>{children}</div>
  );
}

function SectionTitle({ children, icon, th }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
      {icon && <span style={{ fontSize: 16, color: TEAL }}>{icon}</span>}
      <span style={{ fontSize: 15, fontWeight: 700, color: th.text2, letterSpacing: "-0.01em" }}>{children}</span>
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────
function Table({ columns, rows, th, emptyLabel = "No data available" }) {
  return (
    <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${th.cardBorder}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: th.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
            {columns.map((col, i) => (
              <th
                key={i}
                style={{
                  padding: "10px 14px", color: th.text4, fontWeight: 600, fontSize: 11,
                  textAlign: col.right ? "right" : "left",
                  borderBottom: `1px solid ${th.cardBorder}`,
                  letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap",
                }}
              >{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: "24px 14px", textAlign: "center", color: th.text5, fontSize: 12.5 }}>
                {emptyLabel}
              </td>
            </tr>
          ) : (
            rows.map((row, ri) => (
              <tr
                key={ri} style={{ borderBottom: `1px solid ${th.cardBorder}`, transition: "background 0.15s" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = th.rowHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {columns.map((col, ci) => (
                  <td
                    key={ci}
                    style={{
                      padding: "12px 14px", color: col.color ? col.color(row) : th.text2,
                      textAlign: col.right ? "right" : "left", fontWeight: col.bold ? 600 : 400,
                    }}
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ status }) {
  const map = {
    DRAFT: { bg: "rgba(245,158,11,0.12)", color: "#F59E0B", label: "Draft" },
    APPROVED: { bg: "rgba(16,185,129,0.12)", color: "#10B981", label: "Approved" },
    PENDING: { bg: "rgba(100,116,139,0.12)", color: "#64748B", label: "Pending" },
  };
  const s = map[status?.toUpperCase()] || map.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.color, display: "inline-block" }} />
      {s.label}
    </span>
  );
}

// ─── Camp status pill ─────────────────────────────────────────────────────────
function CampStatusPill({ camp }) {
  if (camp.is_completed)
    return <span style={{ padding: "2px 9px", borderRadius: 10, background: "rgba(16,185,129,0.12)", color: "#10B981", fontSize: 10, fontWeight: 700 }}>✓ Completed</span>;
  if (camp.ready_to_go)
    return <span style={{ padding: "2px 9px", borderRadius: 10, background: "rgba(17,168,164,0.14)", color: TEAL, fontSize: 10, fontWeight: 700 }}>● Ready</span>;
  return <span style={{ padding: "2px 9px", borderRadius: 10, background: "rgba(245,158,11,0.12)", color: "#F59E0B", fontSize: 10, fontWeight: 700 }}>◐ Scheduled</span>;
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ value, max, color, th }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, height: 7, background: th.trackBg, borderRadius: 99 }}>
        <div style={{ width: pct + "%", height: "100%", background: color, borderRadius: 99, transition: "width 0.6s cubic-bezier(.4,0,.2,1)" }} />
      </div>
      <span style={{ fontSize: 11, color: th.text4, minWidth: 34, textAlign: "right", fontWeight: 600 }}>{pct}%</span>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ th }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "72px 0", gap: 20 }}>
      <div style={{
        width: 72, height: 72, borderRadius: 20, background: "rgba(17,168,164,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32,
      }}>🏕</div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: th.text1, marginBottom: 8 }}>Pick a camp to get started</div>
        <div style={{ fontSize: 14, color: th.text4, maxWidth: 320, lineHeight: 1.6 }}>
          Use the search dropdown above to find and select any camp — its full financial summary will appear here.
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
        {["💰 Billing", "📦 Costs", "📈 Profit"].map((t) => (
          <span key={t} style={{
            padding: "6px 14px", borderRadius: 20, background: th.isDark ? "rgba(17,168,164,0.08)" : "rgba(17,168,164,0.07)",
            color: TEAL, fontSize: 12, fontWeight: 600, border: `1px solid rgba(17,168,164,0.2)`,
          }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function Skeleton({ h = 16, w = "100%", style, th }) {
  return <div style={{ height: h, width: w, borderRadius: 8, background: th.trackBg, animation: "pulse 1.5s ease-in-out infinite", ...style }} />;
}

// ─── Highlight ────────────────────────────────────────────────────────────────
function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(17,168,164,0.25)", color: TEAL, borderRadius: 3, padding: "0 2px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ─── Theme toggle ─────────────────────────────────────────────────────────────
function ThemeToggle({ isDark, onToggle }) {
  return (
    <button
      onClick={onToggle} title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        display: "flex", alignItems: "center", gap: 7, padding: "7px 13px",
        borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
        background: isDark ? "#1E293B" : "#E2E8F0",
        border: isDark ? "1px solid #334155" : "1px solid #CBD5E1",
        color: isDark ? "#94A3B8" : "#475569",
        transition: "all 0.25s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.color = TEAL; }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isDark ? "#334155" : "#CBD5E1";
        e.currentTarget.style.color = isDark ? "#94A3B8" : "#475569";
      }}
    >
      {isDark ? (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>Light
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>Dark
        </>
      )}
    </button>
  );
}

// ─── Custom Camp Dropdown ─────────────────────────────────────────────────────
function CampDropdown({ camps, selectedCampId, onChange, loading, th }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = camps.find((c) => String(c.id) === String(selectedCampId));

  const filtered = camps.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [c.name, c.camp_name, c.location, c.district, c.state, c.client_name]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 60);
    if (!open) setSearch("");
  }, [open]);

  return (
    <div ref={ref} style={{ flex: 1, position: "relative" }}>
      {/* Trigger button */}
      <div
        onClick={() => !loading && setOpen((o) => !o)}
        style={{
          background: th.inputBg, border: `1.5px solid ${open ? TEAL : th.inputBorder}`,
          borderRadius: 12, padding: "10px 16px", cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          transition: "border-color 0.2s, box-shadow 0.2s", minHeight: 48,
          boxShadow: open ? `0 0 0 3px rgba(17,168,164,0.12)` : "none",
        }}
      >
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%", border: `2px solid ${TEAL}`,
              borderTopColor: "transparent", animation: "spin 0.8s linear infinite",
            }} />
            <span style={{ fontSize: 14, color: th.text4 }}>Loading camps…</span>
          </div>
        ) : selected ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: "rgba(17,168,164,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            }}>🏕</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: th.text1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selected.name || selected.camp_name || `Camp #${selected.id}`}
              </div>
              <div style={{ fontSize: 11, color: th.text4, display: "flex", gap: 8, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                {selected.location && <span>📍 {selected.location}{selected.district ? `, ${selected.district}` : ""}</span>}
                {selected.start_date && <span>🗓 {fmtDate(selected.start_date)} – {fmtDate(selected.end_date)}</span>}
              </div>
            </div>
            <CampStatusPill camp={selected} />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={th.text4} strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <span style={{ fontSize: 14, color: th.text4 }}>Search and select a camp…</span>
          </div>
        )}
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={th.text4} strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.22s" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0,
          background: th.dropdownBg, border: `1.5px solid ${TEAL}`, borderRadius: 14, zIndex: 200,
          boxShadow: `0 12px 40px rgba(0,0,0,${th.isDark ? "0.6" : "0.15"}), 0 0 0 1px rgba(17,168,164,0.08)`,
          animation: "dropIn 0.18s cubic-bezier(.4,0,.2,1)", overflow: "hidden",
        }}>
          {/* Search bar */}
          <div style={{
            padding: "12px 12px 8px", borderBottom: `1px solid ${th.cardBorder}`,
            background: th.isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)", position: "sticky", top: 0, zIndex: 10,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 9,
              background: th.inputBg, border: `1.5px solid ${search ? TEAL : th.inputBorder}`,
              borderRadius: 10, padding: "9px 12px", transition: "border-color 0.2s, box-shadow 0.2s",
              boxShadow: search ? `0 0 0 3px rgba(17,168,164,0.1)` : "none",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={search ? TEAL : th.text4} strokeWidth="2" style={{ flexShrink: 0, transition: "stroke 0.2s" }}>
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={searchRef} type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Type a camp name, location or client…"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: th.text1, fontSize: 13, fontFamily: "inherit" }}
              />
              {search && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSearch(""); searchRef.current?.focus(); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: th.text4, padding: "0 2px", fontSize: 18, lineHeight: 1, display: "flex", alignItems: "center" }}
                >×</button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: th.text5, paddingLeft: 2 }}>
              {search
                ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${search}"`
                : `${camps.length} camp${camps.length !== 1 ? "s" : ""} available`}
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {camps.length === 0 ? (
              <div style={{ padding: "28px 16px", textAlign: "center", color: th.text4, fontSize: 13 }}>No camps found</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: th.text2, marginBottom: 4 }}>No matches for <span style={{ color: TEAL }}>"{search}"</span></div>
                <div style={{ fontSize: 12, color: th.text4 }}>Try searching by location, district, or client name</div>
              </div>
            ) : (
              filtered.map((c, i) => {
                const isSelected = String(c.id) === String(selectedCampId);
                const campName = c.name || c.camp_name || `Camp #${c.id}`;
                return (
                  <div
                    key={c.id}
                    onClick={() => { onChange(c.id); setOpen(false); setSearch(""); }}
                    style={{
                      padding: "13px 16px", cursor: "pointer",
                      borderBottom: i < filtered.length - 1 ? `1px solid ${th.cardBorder}` : "none",
                      background: isSelected ? "rgba(17,168,164,0.08)" : "transparent",
                      display: "flex", alignItems: "center", gap: 12, transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = th.rowHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "rgba(17,168,164,0.08)" : "transparent"; }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                      background: isSelected ? "rgba(17,168,164,0.18)" : th.isDark ? "rgba(148,163,184,0.07)" : "rgba(0,0,0,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
                    }}>🏕</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: isSelected ? TEAL : th.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Highlight text={campName} query={search} />
                        </span>
                        <CampStatusPill camp={c} />
                      </div>
                      <div style={{ fontSize: 11, color: th.text4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {c.location && <span>📍 <Highlight text={`${c.location}${c.district ? `, ${c.district}` : ""}${c.state ? `, ${c.state}` : ""}`} query={search} /></span>}
                        {c.start_date && <span>🗓 {fmtDate(c.start_date)} – {fmtDate(c.end_date)}</span>}
                        {c.client_name && <span>👤 <Highlight text={c.client_name} query={search} /></span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: th.text5, fontFamily: "monospace", marginBottom: 2 }}>#{c.id}</div>
                      {isSelected && (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5">
                          <path d="M5 12l5 5L20 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function CampFinancialDashboard() {
  const [isDark, setIsDark] = useState(true);
  const th = isDark ? DARK : LIGHT;

  const [camps, setCamps] = useState([]);
  const [selectedCampId, setSelectedCampId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loadingCamps, setLoadingCamps] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => { fetchCamps(); }, []);
  useEffect(() => {
    if (selectedCampId) fetchSummary(selectedCampId);
    else setSummary(null);
  }, [selectedCampId]);

  async function fetchCamps() {
    setLoadingCamps(true);
    try {
      const res = await fetch(`${BASE}/camps/`);
      const data = await res.json();
      setCamps(Array.isArray(data) ? data : data.results || data.camps || []);
    } catch { setError("Couldn't load camps — please check your connection."); }
    finally { setLoadingCamps(false); }
  }

  async function fetchSummary(id) {
    setLoadingSummary(true); setError(null);
    try {
      const res = await fetch(`${BASE}/technician/camp-financial-summary/${id}/`);
      if (!res.ok) throw new Error();
      setSummary(await res.json());
      setLastRefreshed(new Date());
    } catch { setError("Couldn't load the summary for this camp. Please try again."); setSummary(null); }
    finally { setLoadingSummary(false); }
  }

  function handleLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    sessionStorage.clear();
    window.location.href = "/login";
  }

  const s = summary?.summary;
  const billing = summary?.billing_breakdown || [];
  const costs = summary?.cost_breakdown || [];
  const isLoss = s ? parseFloat(s.profit_amount) < 0 : false;
  const totalBilling = s ? parseFloat(s.total_billing) || billing.reduce((sum, b) => sum + Number(b.billing_amount || 0), 0) : 0;
  const totalCost = s ? parseFloat(s.total_cost) : 0;
  const grossResource = s ? parseFloat(s.gross_resource_cost) : 0;
  const totalExpense = s ? parseFloat(s.total_expense) : 0;

  return (
    <div style={{
      minHeight: "100vh", background: th.pageBg,
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif", color: th.text2,
      transition: "background 0.3s, color 0.3s",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dropIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to { transform: rotate(360deg) } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${th.isDark ? "#1E293B" : "#CBD5E1"}; border-radius: 99px; }
        ::-webkit-scrollbar-thumb:hover { background: ${TEAL}; }
        .anim-in { animation: fadeIn 0.35s cubic-bezier(.4,0,.2,1); }
        .summary-row { display:flex; align-items:center; justify-content:space-between; padding:11px 0; border-bottom:1px solid ${th.cardBorder}; }
        .summary-row:last-child { border-bottom:none; }
      `}</style>

      {/* ── Nav bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: th.navBg, borderBottom: `1px solid ${th.navBorder}`,
        padding: "0 28px", height: 62, display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50,
        boxShadow: th.isDark ? "none" : "0 1px 8px rgba(0,0,0,0.06)",
        transition: "background 0.3s, border-color 0.3s",
      }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: `linear-gradient(135deg, ${TEAL}, #0a6f6c)`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: "0 2px 8px rgba(17,168,164,0.35)",
          }}>⚕</div>
          <div>
            <span style={{ fontSize: 15, fontWeight: 800, color: th.text1, letterSpacing: "-0.02em" }}>Camp Finance</span>
            <span style={{
              marginLeft: 8, background: "rgba(17,168,164,0.14)", color: TEAL,
              borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
            }}>Dashboard</span>
          </div>
        </div>

        {/* Right controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {lastRefreshed && (
            <div style={{ fontSize: 11, color: th.text5, display: "flex", alignItems: "center", gap: 5 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              {lastRefreshed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
          <ThemeToggle isDark={isDark} onToggle={() => setIsDark((d) => !d)} />
          <button
            onClick={handleLogout}
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "7px 14px",
              borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
              background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.22)",
              color: "#EF4444", transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.14)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.4)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.07)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.22)"; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      </div>

      {/* ── Page body ────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "32px 24px" }}>

        {/* Camp selector */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: th.text4, marginBottom: 8, letterSpacing: "0.02em" }}>
            SELECT CAMP
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CampDropdown
              camps={camps} selectedCampId={selectedCampId}
              onChange={(id) => setSelectedCampId(String(id))} loading={loadingCamps} th={th}
            />
            <button
              onClick={() => selectedCampId && fetchSummary(selectedCampId)}
              disabled={!selectedCampId || loadingSummary}
              title="Refresh summary"
              style={{
                background: th.cardBg, border: `1.5px solid ${th.cardBorder}`, borderRadius: 12,
                padding: "0 16px", height: 48, cursor: !selectedCampId || loadingSummary ? "not-allowed" : "pointer",
                color: th.text3, fontSize: 18, display: "flex", alignItems: "center", transition: "all 0.2s",
                opacity: !selectedCampId ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { if (selectedCampId && !loadingSummary) { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.color = TEAL; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = th.cardBorder; e.currentTarget.style.color = th.text3; }}
            >
              <span style={{ display: "inline-block", animation: loadingSummary ? "spin 0.8s linear infinite" : "none" }}>↻</span>
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 12, padding: "14px 18px", color: "#EF4444", fontSize: 13, marginBottom: 24,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Something went wrong</div>
              <div style={{ opacity: 0.85 }}>{error}</div>
            </div>
          </div>
        )}

        {/* Skeletons */}
        {loadingSummary && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 28 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ background: th.cardBg, borderRadius: 16, padding: 22, border: `1px solid ${th.cardBorder}` }}>
                  <Skeleton h={11} w="55%" style={{ marginBottom: 16 }} th={th} />
                  <Skeleton h={30} w="75%" th={th} />
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[1, 2].map((i) => (
                <div key={i} style={{ background: th.cardBg, borderRadius: 16, padding: 22, border: `1px solid ${th.cardBorder}` }}>
                  <Skeleton h={14} w="45%" style={{ marginBottom: 18 }} th={th} />
                  {[1, 2, 3].map((j) => <Skeleton key={j} h={10} style={{ marginBottom: 16 }} th={th} />)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loadingSummary && !summary && !error && <EmptyState th={th} />}

        {/* ── Dashboard ──────────────────────────────────────────────────────── */}
        {!loadingSummary && summary && s && (
          <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 28 }}>

            {/* Camp header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 800, color: th.text1, margin: 0, letterSpacing: "-0.02em" }}>{s.camp_name}</h1>
                  <span style={{
                    fontSize: 11, color: th.text4, fontFamily: "monospace",
                    background: th.isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    padding: "2px 8px", borderRadius: 6,
                  }}>#{s.camp}</span>
                </div>
                <p style={{ fontSize: 13, color: th.text4, margin: 0, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <span>👤 <span style={{ color: th.text3 }}>{s.client_name}</span></span>
                  <span style={{ color: th.cardBorder }}>·</span>
                  <span>🕐 <span style={{ color: th.text3 }}>{new Date(s.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span></span>
                </p>
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 12,
                background: isLoss ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.08)",
                border: `1.5px solid ${isLoss ? "rgba(239,68,68,0.2)" : "rgba(16,185,129,0.2)"}`,
                color: isLoss ? "#EF4444" : "#10B981",
              }}>
                <span style={{ fontSize: 20 }}>{isLoss ? "📉" : "📈"}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.05em" }}>{isLoss ? "Net Loss" : "Net Profit"}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em" }}>{fmtPct(s.profit_percentage)}</div>
                </div>
              </div>
            </div>

            {/* KPI grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(215px, 1fr))", gap: 14 }}>
              <KpiCard icon="💰" label="Total Billing" value={fmt(s.total_billing)} sub={`${billing.length} line item${billing.length !== 1 ? "s" : ""} billed`} accent th={th} />
              <KpiCard icon="📦" label="Total Cost" value={fmt(s.total_cost)} sub="Resources + all expenses" th={th} />
              <KpiCard icon="🏦" label="Resource Cost" value={fmt(s.gross_resource_cost)} sub="Gross, before deductions" th={th} />
              <KpiCard
                icon={isLoss ? "📉" : "📈"} label="Net Profit / Loss"
                value={fmt(s.profit_amount)} sub={`${fmtPct(s.profit_percentage)} margin`}
                danger={isLoss} success={!isLoss} th={th}
              />
            </div>

            {/* Billing breakdown + donut */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16, alignItems: "start" }}>
              <Card th={th}>
                <SectionTitle icon="🧾" th={th}>Billing Breakdown</SectionTitle>
                <Table
                  th={th}
                  emptyLabel="No billing line items for this camp yet."
                  columns={[
                    { key: "component", label: "Component" },
                    { key: "units", label: "Units", right: true },
                    { key: "rate", label: "Rate / unit", right: true, render: (r) => `₹${r.rate}` },
                    { key: "billing_amount", label: "Amount", right: true, bold: true, render: (r) => fmt(r.billing_amount), color: () => TEAL },
                  ]}
                  rows={billing}
                />
                {billing.length > 0 && (
                  <>
                    <div style={{ marginTop: 22 }}>
                      {billing.map((b, i) => (
                        <div key={i} style={{ marginBottom: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                            <span style={{ fontSize: 12, color: th.text3, fontWeight: 500 }}>{b.component}</span>
                            <span style={{ fontSize: 12, color: TEAL, fontWeight: 700 }}>{fmt(b.billing_amount)}</span>
                          </div>
                          <ProgressBar value={b.billing_amount} max={totalBilling} color={BAR_COLORS[i % BAR_COLORS.length]} th={th} />
                        </div>
                      ))}
                    </div>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginTop: 16, padding: "14px 0 0", borderTop: `1.5px solid ${th.cardBorder}`,
                    }}>
                      <span style={{ fontSize: 13, color: th.text3, fontWeight: 600 }}>Total Billing</span>
                      <span style={{ fontSize: 17, fontWeight: 800, color: TEAL }}>{fmt(s.total_billing)}</span>
                    </div>
                  </>
                )}
              </Card>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Card th={th}>
                  <SectionTitle icon="🍩" th={th}>Billing Mix</SectionTitle>
                  {billing.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: th.text5, textAlign: "center", padding: "12px 0" }}>No billing components yet.</div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <DonutChart size={110} segments={billing.map((b, i) => ({ value: b.billing_amount, color: BAR_COLORS[i % BAR_COLORS.length] }))} th={th} />
                      <div style={{ flex: 1 }}>
                        {billing.map((b, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                            <div style={{ width: 9, height: 9, borderRadius: 3, background: BAR_COLORS[i % BAR_COLORS.length], flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: th.text3, flex: 1 }}>{b.component}</span>
                            <span style={{ fontSize: 11, color: th.text4, fontWeight: 700 }}>
                              {totalBilling > 0 ? Math.round((b.billing_amount / totalBilling) * 100) : 0}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                <Card th={th}>
                  <SectionTitle icon="📊" th={th}>Units by Component</SectionTitle>
                  {billing.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: th.text5, textAlign: "center", padding: "12px 0" }}>No units to chart yet.</div>
                  ) : (
                    <>
                      <MiniBarChart data={billing.map((b) => ({ label: b.component, value: b.units }))} colors={BAR_COLORS} th={th} />
                      <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap" }}>
                        {billing.map((b, i) => (
                          <div key={i} style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: BAR_COLORS[i % BAR_COLORS.length] }}>{b.units}</div>
                            <div style={{ fontSize: 10, color: th.text4, marginTop: 2 }}>{b.component}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Card>
              </div>
            </div>

            {/* Cost breakdown table */}
            <Card th={th}>
              <SectionTitle icon="👷" th={th}>Cost Breakdown by Technician</SectionTitle>
              <Table
                th={th}
                emptyLabel="No technician costs recorded for this camp yet."
                columns={[
                  { key: "technician", label: "Technician" },
                  { key: "gross_resource_cost", label: "Resource Cost", right: true, render: (r) => fmt(r.gross_resource_cost) },
                  { key: "submitted_expense", label: "Submitted Exp.", right: true, render: (r) => fmt(r.submitted_expense) },
                  { key: "fixed_expense", label: "Fixed Exp.", right: true, render: (r) => fmt(r.fixed_expense) },
                  { key: "total_expense", label: "Total Exp.", right: true, render: (r) => fmt(r.total_expense) },
                  { key: "advance_paid", label: "Advance Paid", right: true, render: (r) => fmt(r.advance_paid), color: () => "#F59E0B" },
                  { key: "net_payable", label: "Net Payable", right: true, bold: true, render: (r) => fmt(r.net_payable), color: (r) => parseFloat(r.net_payable) < 0 ? "#EF4444" : "#10B981" },
                  { key: "status", label: "Status", right: true, render: (r) => <Badge status={r.status} /> },
                ]}
                rows={costs}
              />
            </Card>

            {/* P&L + Cost composition */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Card th={th}>
                <SectionTitle icon="⚖️" th={th}>P&L Summary</SectionTitle>
                {[
                  { label: "Total Billing", value: fmt(s.total_billing), color: TEAL },
                  { label: "Gross Resource Cost", value: fmt(s.gross_resource_cost), color: th.text3 },
                  { label: "Total Expense", value: fmt(s.total_expense), color: th.text3 },
                  { label: "Total Cost", value: fmt(s.total_cost), color: th.text2 },
                  { label: isLoss ? "Net Loss" : "Net Profit", value: `${fmt(s.profit_amount)} (${fmtPct(s.profit_percentage)})`, color: isLoss ? "#EF4444" : "#10B981", big: true },
                ].map((row, i) => (
                  <div key={i} className="summary-row">
                    <span style={{ fontSize: 13, color: th.text4 }}>{row.label}</span>
                    <span style={{ fontSize: row.big ? 15 : 13, fontWeight: row.big ? 800 : 500, color: row.color }}>{row.value}</span>
                  </div>
                ))}
              </Card>

              <Card th={th}>
                <SectionTitle icon="🔍" th={th}>Cost Composition</SectionTitle>
                {totalCost === 0 ? (
                  <div style={{ fontSize: 12.5, color: th.text5, textAlign: "center", padding: "12px 0" }}>No costs recorded for this camp yet.</div>
                ) : (
                  [
                    { label: "Resource Cost", value: grossResource, color: TEAL },
                    { label: "Total Expenses", value: totalExpense, color: "#F59E0B" },
                  ].map((item, i) => (
                    <div key={i} style={{ marginBottom: 20 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 9 }}>
                        <span style={{ fontSize: 13, color: th.text3, fontWeight: 500 }}>{item.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: item.color }}>{fmt(item.value)}</span>
                      </div>
                      <ProgressBar value={item.value} max={totalCost} color={item.color} th={th} />
                    </div>
                  ))
                )}
                <div style={{ marginTop: 20, padding: "16px 0 0", borderTop: `1.5px solid ${th.cardBorder}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: th.text4, fontWeight: 500 }}>Total Cost</span>
                    <span style={{ fontSize: 17, fontWeight: 800, color: th.text1 }}>{fmt(s.total_cost)}</span>
                  </div>
                </div>
              </Card>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}